import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/common/Sidebar';
import { useAuth } from '@/hooks/useAuth';
import { EHRHeader } from '@/components/ehr/EHRHeader';
import { AnamneseSection } from '@/components/ehr/AnamneseSection';
import { EvolutionTimeline } from '@/components/ehr/EvolutionTimeline';
import { PrescriptionWidget } from '@/components/ehr/PrescriptionWidget';
import { DiagnosisWidget } from '@/components/ehr/DiagnosisWidget';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserRole } from '@/types/auth';
import { useParams } from 'react-router-dom';
import { Calendar, User, ClipboardList, Activity } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import { SafeContent } from '@/components/common/SafeContent';
import { downloadPatientRecordPdf } from '@/services/pdfExports';
import { sanitizeText } from '@/lib/sanitize';
import { formatPatientNationality } from '@/lib/patientProntuarioFields';
import { formatAppointmentReasonForDisplay } from '@/lib/appointmentReason';
import { toast } from 'sonner';

type PatientSummaryPatient = {
  id: string;
  patientProfileId?: string;
  name?: string;
  email?: string;
  phone?: string;
  cpf?: string;
  dateOfBirth?: string;
  gender?: string;
  birthPlace?: string;
  isBrazilian?: boolean;
  country?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  bloodType?: string;
  height?: number;
  weight?: number;
  allergies?: string;
  medications?: string;
  medicalHistory?: string;
};

type PatientSummaryClosure = {
  id: string;
  clinicalSummary?: string;
  finalNotes?: string;
  patientGuidance?: string;
  followUpRequired?: boolean;
  followUpType?: 'days' | 'weeks' | 'months' | 'exact' | string | null;
  suggestedFollowUpDate?: string | null;
  noFollowUpNeeded?: boolean;
  internalObservation?: string;
};

interface PatientSummary {
  patient: PatientSummaryPatient | null;
  lastConsultation: {
    date: string;
    summary?: string;
    diagnosis?: string;
    closure?: PatientSummaryClosure | null;
  } | null;
  history: Array<{
    date: string;
    doctor: string;
    procedure: string;
    closure?: PatientSummaryClosure | null;
  }>;
  cumulativeContent: string;
}

export default function EHRPage() {
  const { user, logout, isLoading: isAuthLoading } = useAuth();
  const { pacienteId } = useParams();
  const [activeTab, setActiveTab] = useState('prontuario');
  const [summary, setSummary] = useState<PatientSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      console.log('[EHRPage] 🔄 fetchSummary triggered:', { 
        pacienteId, 
        userId: user?.id, 
        userRole: user?.role,
        isAuthLoading 
      });

      // Wait for auth to finish loading before deciding what to fetch
      if (isAuthLoading) {
        console.log('[EHRPage] ⏳ Waiting for auth to load...');
        return;
      }
      
      const idToFetch = pacienteId || user?.id;
      if (!idToFetch) {
        console.warn('[EHRPage] ⚠️ No ID to fetch. pacienteId and user.id are missing.');
        setLoading(false);
        setError('Identificação do paciente não encontrada.');
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        console.log(`[EHRPage] 📡 Fetching summary for ID: ${idToFetch}`);
        const response = await apiClient.get<PatientSummary>(
          API_ENDPOINTS.MEDICAL_RECORDS.PATIENT_SUMMARY(idToFetch),
        );
        
        console.log('[EHRPage] 📥 API Response:', response);

        if (response.success && response.data) {
          setSummary(response.data);
        } else {
          console.error('[EHRPage] ❌ Erro ao buscar resumo:', response.error);
          setError(response.error || 'Erro ao carregar prontuário ou vínculo inexistente.');
        }
      } catch (err) {
        console.error('[EHRPage] 💥 Exceção ao buscar resumo:', err);
        setError('Falha na comunicação com o servidor.');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [pacienteId, user?.id, isAuthLoading]);

  const isReadOnly = user?.role === UserRole.PATIENT;
  const patientIdForExport = pacienteId || user?.id || '';

  const handleExportPdf = async () => {
    if (!patientIdForExport) {
      toast.error('Identificação do paciente não encontrada.');
      return;
    }

    try {
      setIsExportingPdf(true);
      await downloadPatientRecordPdf(patientIdForExport);
      toast.success('PDF do prontuário gerado com sucesso.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar PDF do prontuário.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Always show Sidebar for standard app look
  const renderLayout = (content: React.ReactNode) => (
    <div className="flex min-h-screen bg-gray-50 dark:bg-neutral-950">
      <Sidebar 
        userRole={user?.role || UserRole.PATIENT}
        userName={user?.name || 'Usuário'} 
        onLogout={logout} 
      />
      <main className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {content}
        </div>
      </main>
    </div>
  );

  if (loading) {
    return renderLayout(
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
        <div className="text-teal-600 font-bold">Carregando Prontuário...</div>
      </div>
    );
  }

  if (error || !summary) {
    return renderLayout(
      <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-neutral-900 rounded-3xl border border-dashed border-gray-200 dark:border-neutral-800">
        <div className="text-red-600 font-bold text-xl mb-2">Ops! Algo deu errado</div>
        <div className="text-gray-500">{error || 'Prontuário não encontrado ou você não tem permissão para acessá-lo.'}</div>
        <button 
          onClick={() => window.location.reload()}
          className="mt-6 px-6 py-2 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition-colors"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  const patientWeight = summary.patient?.weight;
  const patientHeight = summary.patient?.height;
  const patientImc = patientWeight && patientHeight && patientHeight > 0
    ? (patientWeight / (patientHeight * patientHeight)).toFixed(2)
    : null;

  const patientData = {
    name: summary.patient?.name || 'Paciente',
    birthDate: summary.patient?.dateOfBirth ? new Date(summary.patient.dateOfBirth).toLocaleDateString() : 'Não informada',
    age: summary.patient?.dateOfBirth ? new Date().getFullYear() - new Date(summary.patient.dateOfBirth).getFullYear() : 0,
    gender: summary.patient?.gender || 'Não informado',
    birthPlace: formatPatientNationality(summary.patient?.isBrazilian, summary.patient?.country),
    address: summary.patient?.street 
      ? `${summary.patient.street}, ${summary.patient.number}${summary.patient.complement ? ` - ${summary.patient.complement}` : ''}, ${summary.patient.neighborhood}, ${summary.patient.city} - ${summary.patient.state}`
      : 'Não informado',
    status: "PACIENTE ATIVO",
    initials: (summary.patient?.name || 'P').charAt(0).toUpperCase()
  };

  return renderLayout(
    <>
      {/* Breadcrumb-ish */}
      <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">
        <span>Paciente</span>
        <span className="text-teal-600">/</span>
        <span className="text-teal-600">Prontuário Eletrônico Centrado no Paciente</span>
      </div>

      <EHRHeader
        patient={patientData}
        onExportPdf={handleExportPdf}
        isExportingPdf={isExportingPdf}
      />

      {/* Resumo da Última Consulta */}
      {summary.lastConsultation && (
        <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-900/30 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-white dark:bg-neutral-900 rounded-lg text-teal-600">
              <ClipboardList className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Resumo da Última Consulta</h2>
            <span className="text-xs text-gray-500 font-medium">
              {new Date(summary.lastConsultation.date).toLocaleDateString()}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase mb-1">Conduta Clínica</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {summary.lastConsultation.summary || 'Nenhum resumo disponível.'}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase mb-1">Diagnóstico Principal</p>
              <div className="inline-flex items-center px-3 py-1 rounded-lg bg-white dark:bg-neutral-900 border border-teal-100 text-teal-700 font-bold text-sm">
                {summary.lastConsultation.diagnosis || 'Não informado'}
              </div>
            </div>
          </div>

          {summary.lastConsultation.closure && (
            <div className="mt-6 pt-6 border-t border-teal-100/50 dark:border-teal-900/10 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              {summary.lastConsultation.closure.patientGuidance && (
                <div className="md:col-span-1">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Orientações ao Paciente</p>
                  <p className="text-gray-750 dark:text-gray-300 leading-relaxed">{summary.lastConsultation.closure.patientGuidance}</p>
                </div>
              )}
              {summary.lastConsultation.closure.finalNotes && (
                <div className="md:col-span-1">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Observações Clínicas Finais</p>
                  <p className="text-gray-750 dark:text-gray-300 leading-relaxed">{summary.lastConsultation.closure.finalNotes}</p>
                </div>
              )}
              <div className="md:col-span-1">
                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Necessidade de Retorno</p>
                {summary.lastConsultation.closure.followUpRequired ? (
                  <p className="text-teal-700 dark:text-teal-400 font-bold">
                    Recomendado: {summary.lastConsultation.closure.followUpType === 'exact' && summary.lastConsultation.closure.suggestedFollowUpDate
                      ? `Sugerido para ${new Date(summary.lastConsultation.closure.suggestedFollowUpDate).toLocaleDateString()}` 
                      : `Prazo sugerido (${summary.lastConsultation.closure.followUpType})`}
                  </p>
                ) : (
                  <p className="text-gray-500">Sem necessidade de retorno imediato.</p>
                )}
              </div>
              {summary.lastConsultation.closure.internalObservation && (
                <div className="bg-amber-50/50 dark:bg-amber-950/10 p-4 rounded-xl border border-amber-200/30 dark:border-amber-900/10 md:col-span-3">
                  <p className="text-xs font-bold text-amber-800 dark:text-amber-400 uppercase mb-1 flex items-center gap-1.5">
                    <span>Observações Internas (Confidencial para Médicos)</span>
                  </p>
                  <p className="text-amber-900 dark:text-amber-300 font-medium text-xs leading-relaxed">{summary.lastConsultation.closure.internalObservation}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Tabs defaultValue="prontuario" className="space-y-8" onValueChange={setActiveTab}>
        <TabsList className="bg-transparent border-b border-gray-200 dark:border-neutral-800 w-full justify-start rounded-none h-auto p-0 gap-8">
          <TabsTrigger 
            value="prontuario" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent data-[state=active]:text-teal-600 px-0 py-4 text-sm font-bold transition-all"
          >
            Prontuário Cumulativo
          </TabsTrigger>
          <TabsTrigger 
            value="historico" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-600 data-[state=active]:bg-transparent data-[state=active]:text-teal-600 px-0 py-4 text-sm font-bold transition-all"
          >
            Histórico de Consultas
          </TabsTrigger>
        </TabsList>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <TabsContent value="prontuario" className="m-0 focus-visible:outline-none">
              <div className="bg-white dark:bg-neutral-900 p-8 rounded-2xl border border-gray-100 dark:border-neutral-800 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Evolução Clínica Agregada</h3>
                <SafeContent
                  content={summary.cumulativeContent || 'Nenhum registro anterior encontrado.'}
                  className="text-gray-700 dark:text-gray-300 leading-relaxed"
                  showSkeleton={false}
                />
                {summary.cumulativeContent ? (
                  <SafeContent
                    content={toDisplayClinicalHtml(summary.cumulativeContent)}
                    className="text-gray-700 dark:text-gray-300 leading-relaxed"
                    showSkeleton={false}
                  />
                ) : (
                  <p className="text-gray-500">Nenhum registro anterior encontrado.</p>
                )}
              </div>
            </TabsContent>
            
          <TabsContent value="historico" className="m-0 focus-visible:outline-none">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 shadow-sm overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-neutral-800/50">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">Data</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">Médico</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">Procedimento/Motivo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                  {summary.history.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50/50 dark:hover:bg-neutral-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {new Date(item.date).toLocaleDateString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{item.doctor}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatAppointmentReasonForDisplay(item.procedure)}
                        </span>
                        {item.closure && (
                          <div className="mt-3 text-xs space-y-1.5 bg-neutral-50 dark:bg-neutral-800/40 p-3 rounded-xl border border-neutral-100 dark:border-neutral-800/60 max-w-xl">
                            {item.closure.clinicalSummary && (
                              <p className="text-gray-750 dark:text-gray-300"><strong>Resumo Clínico:</strong> {item.closure.clinicalSummary}</p>
                            )}
                            {item.closure.patientGuidance && (
                              <p className="text-gray-750 dark:text-gray-300"><strong>Orientações:</strong> {item.closure.patientGuidance}</p>
                            )}
                            {item.closure.followUpRequired && (
                              <p className="text-teal-700 dark:text-teal-400">
                                <strong>Recomendação de Retorno:</strong> {item.closure.followUpType === 'exact' && item.closure.suggestedFollowUpDate
                                  ? `Data: ${new Date(item.closure.suggestedFollowUpDate).toLocaleDateString()}` 
                                  : `Prazo (${item.closure.followUpType})`}
                              </p>
                            )}
                            {item.closure.internalObservation && (
                              <p className="text-amber-800 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-950/20 p-2 rounded-lg border border-amber-200/30 dark:border-amber-900/10">
                                <strong>Anotação Clínica Interna:</strong> {item.closure.internalObservation}
                              </p>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {summary.history.length === 0 && (
                <div className="py-20 text-center">
                  <Activity className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhuma consulta anterior registrada.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </div>

        <div className="space-y-8">
          <DiagnosisWidget 
            primary={summary.lastConsultation?.diagnosis || "Nenhum diagnóstico recente"} 
            definitive="Histórico cumulativo disponível na aba ao lado."
          />
          <div className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-gray-100 dark:border-neutral-800 shadow-sm">
            <h4 className="font-bold text-gray-900 dark:text-white mb-4">Informações Rápidas</h4>
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase">Total de Consultas</p>
                <p className="text-lg font-bold text-teal-600">{summary.history.length}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase">Primeiro Atendimento</p>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {summary.history.length > 0 ? new Date(summary.history[summary.history.length - 1].date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              
              <hr className="border-gray-150 dark:border-neutral-800" />
              
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase">Tipo Sanguíneo</p>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-250">{summary.patient?.bloodType || 'Não informado'}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Altura</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-250">{summary.patient?.height ? `${summary.patient.height} m` : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">Peso</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-250">{summary.patient?.weight ? `${summary.patient.weight} kg` : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase">IMC</p>
                  <p className="text-sm font-semibold text-teal-600">{patientImc || 'N/A'}</p>
                </div>
              </div>

              <hr className="border-gray-150 dark:border-neutral-800" />

              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Alergias</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line bg-gray-50 dark:bg-neutral-850 p-2.5 rounded-lg border border-gray-100 dark:border-neutral-800">
                  {summary.patient?.allergies || 'Nenhuma informada'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Medicamentos em uso</p>
                <p className="text-xs text-gray-600 dark:text-gray-450 leading-relaxed whitespace-pre-line bg-gray-50 dark:bg-neutral-850 p-2.5 rounded-lg border border-gray-100 dark:border-neutral-800">
                  {summary.patient?.medications || 'Nenhum informado'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Doenças Pré-existentes</p>
                <p className="text-xs text-gray-600 dark:text-gray-450 leading-relaxed whitespace-pre-line bg-gray-50 dark:bg-neutral-850 p-2.5 rounded-lg border border-gray-100 dark:border-neutral-800">
                  {summary.patient?.medicalHistory || 'Nenhuma informada'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Tabs>
  </>
  );
}

function toDisplayClinicalHtml(content: string): string {
  const withoutBrokenTags = content.replace(/<\/?[a-zA-Z][^>\n\r]{0,80}(?=\r?\n|$)/g, '');
  const repaired = withoutBrokenTags.replace(/\r?\n/g, '<br>').replace(/(<br>\s*)+$/g, '');

  if (/<\/?[a-z][\s\S]*>/i.test(content)) {
    return repaired;
  }

  return sanitizeText(withoutBrokenTags).replace(/\r?\n/g, '<br>').replace(/(<br>\s*)+$/g, '');
}
